import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useCartStore } from '../stores/cartStore';
import { useAuthStore } from '../stores/authStore';
import { SEGCO_PRIMARY_PRODUCT_CATEGORIES } from '../constants/storeCategories';
import { ShoppingCartIcon, MagnifyingGlassIcon, MapPinIcon } from '@heroicons/react/24/outline';

const Header: React.FC = () => {
  const cartTotalItems = useCartStore((s) =>
    s.cart.reduce((total, item) => total + item.quantity, 0)
  );
  const user = useAuthStore((s) => s.user);
  const sessionLoading = useAuthStore((s) => s.loading || !s.initialized);
  const logout = useAuthStore((s) => s.logout);
  const isAdmin = String(user?.role ?? '').toUpperCase() === 'ADMIN';

  const displayName =
    (typeof user?.name === 'string' && user.name) ||
    (typeof user?.email === 'string' && user.email) ||
    'there';

  const [searchQuery, setSearchQuery] = useState('');
  const [searchCategory, setSearchCategory] = useState('All');
  const navigate = useNavigate();

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const q = searchQuery.trim();
    const params = new URLSearchParams();
    if (searchCategory !== 'All') params.set('category', searchCategory);
    if (q) params.set('search', q);
    const qs = params.toString();
    navigate(qs ? `/products?${qs}` : '/products');
  };

  const handleLogout = async () => {
    await logout();
    navigate('/', { replace: true });
  };

  return (
    <>
      {/* Top Bar */}
      <div className="bg-[#0a66c2] text-white text-sm py-2 sticky top-0 z-50">
        <div className="container mx-auto px-4 flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Link to="/" className="flex items-center">
              <div className="text-base sm:text-xl md:text-2xl font-bold text-white leading-tight">
                Segco Wholesalers
              </div>
            </Link>
            <div className="hidden md:flex items-center text-xs hover:bg-[#004182] cursor-pointer px-2 py-1 rounded">
              <MapPinIcon className="w-4 h-4 mr-1" />
              <div>
                <div className="text-gray-200">Deliver to</div>
                <div className="font-bold">South Africa</div>
              </div>
            </div>
          </div>

          <div className="flex-1 max-w-3xl mx-4 hidden md:flex">
            <form onSubmit={handleSearch} className="flex w-full">
              <select
                aria-label="Search category"
                value={searchCategory}
                onChange={(e) => setSearchCategory(e.target.value)}
                className="max-w-[11rem] shrink-0 bg-gray-100 text-gray-700 px-3 py-2 rounded-l-md border-r border-gray-300 text-sm"
              >
                <option value="All">All</option>
                {SEGCO_PRIMARY_PRODUCT_CATEGORIES.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search Segco Wholesalers"
                className="flex-1 px-4 py-2 border-0 focus:outline-none text-black"
              />
              <button
                type="submit"
                className="bg-[#70b5f9] hover:bg-[#5a9fd9] text-white px-4 py-2 rounded-r-md transition-colors"
              >
                <MagnifyingGlassIcon className="w-5 h-5" />
              </button>
            </form>
          </div>

          <div className="flex items-center space-x-2 sm:space-x-4">
            {sessionLoading ? (
              <div className="hidden md:block text-xs px-2 py-1 text-gray-200">Loading…</div>
            ) : user ? (
              <div className="hidden md:flex items-center gap-2">
                <div className="text-xs px-2 py-1 rounded max-w-[160px]">
                  <div className="text-gray-200 truncate">Hello, {displayName}</div>
                  <div className="font-bold truncate">Account</div>
                </div>
                {isAdmin && (
                  <Link
                    to="/admin"
                    className="text-xs font-bold hover:bg-[#004182] px-2 py-1 rounded whitespace-nowrap"
                  >
                    Admin
                  </Link>
                )}
                <button
                  type="button"
                  onClick={() => void handleLogout()}
                  className="text-xs font-bold hover:bg-[#004182] px-2 py-1 rounded whitespace-nowrap"
                >
                  Sign out
                </button>
              </div>
            ) : (
              <>
                <Link
                  to="/login"
                  className="hidden md:block text-xs hover:bg-[#004182] px-2 py-1 rounded"
                >
                  <div className="text-gray-200">Hello, sign in</div>
                  <div className="font-bold">Sign in</div>
                </Link>
                <Link
                  to="/register"
                  className="hidden md:block text-xs hover:bg-[#004182] px-2 py-1 rounded border-l border-white/25 pl-4"
                >
                  <div className="text-gray-200">New customer?</div>
                  <div className="font-bold">Sign up</div>
                </Link>
              </>
            )}
            <div className="flex md:hidden items-center gap-2 text-xs shrink-0">
              {!sessionLoading && !user && (
                <>
                  <Link to="/login" className="hover:underline font-semibold">
                    Sign in
                  </Link>
                  <span className="text-white/50">|</span>
                  <Link to="/register" className="hover:underline font-semibold">
                    Sign up
                  </Link>
                </>
              )}
              {!sessionLoading && user && (
                <>
                  {isAdmin && (
                    <>
                      <Link to="/admin" className="hover:underline font-semibold">
                        Admin
                      </Link>
                      <span className="text-white/50">|</span>
                    </>
                  )}
                  <button
                    type="button"
                    onClick={() => void handleLogout()}
                    className="hover:underline font-semibold"
                  >
                    Sign out
                  </button>
                </>
              )}
            </div>
            <div className="hidden md:block text-xs hover:bg-[#004182] cursor-pointer px-2 py-1 rounded">
              <div className="text-gray-200">Returns</div>
              <div className="font-bold">& Orders</div>
            </div>
            <Link
              to="/cart"
              className="relative flex items-center hover:bg-[#004182] px-2 py-1 rounded"
            >
              <div className="flex items-end">
                <ShoppingCartIcon className="w-8 h-8" />
                <span className="text-sm font-bold ml-1">Cart</span>
              </div>
              {cartTotalItems > 0 && (
                <span className="absolute -top-1 left-6 bg-[#70b5f9] text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
                  {cartTotalItems}
                </span>
              )}
            </Link>
          </div>
        </div>
      </div>

      {/* Navigation Bar */}
      <div className="bg-[#004182] text-white text-sm sticky top-[42px] z-40">
        <div className="container mx-auto px-4 py-2">
          <div className="flex items-center space-x-4 overflow-x-auto">
            <Link to="/products" className="flex items-center hover:bg-[#0a66c2] cursor-pointer px-2 py-1 rounded shrink-0">
              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
              <span className="font-bold">All</span>
            </Link>
            <Link to="/products?deal=top" className="hover:bg-[#0a66c2] cursor-pointer px-2 py-1 rounded shrink-0">
              Today's Deals
            </Link>
            <Link to="/customer-service" className="hover:bg-[#0a66c2] cursor-pointer px-2 py-1 rounded shrink-0">
              Customer Service
            </Link>
            <Link to="/registry" className="hover:bg-[#0a66c2] cursor-pointer px-2 py-1 rounded shrink-0">
              Registry
            </Link>
            <Link to="/gift-cards" className="hover:bg-[#0a66c2] cursor-pointer px-2 py-1 rounded shrink-0">
              Gift Cards
            </Link>
            <Link to="/register" className="hover:bg-[#0a66c2] cursor-pointer px-2 py-1 rounded font-semibold">
              Sign up
            </Link>
            <Link to="/login" className="hover:bg-[#0a66c2] cursor-pointer px-2 py-1 rounded">
              Sign in
            </Link>
            {isAdmin && (
              <Link to="/admin" className="hover:bg-[#0a66c2] cursor-pointer px-2 py-1 rounded font-semibold">
                Admin
              </Link>
            )}
          </div>
        </div>
      </div>

      {/* Mobile Search */}
      <div className="md:hidden bg-[#0a66c2] px-4 py-2">
        <form onSubmit={handleSearch} className="flex">
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search Segco Wholesalers"
            className="flex-1 px-3 py-2 rounded-l-md border-0 focus:outline-none text-black text-sm"
          />
          <button
            type="submit"
            className="bg-[#70b5f9] hover:bg-[#5a9fd9] text-white px-3 py-2 rounded-r-md transition-colors"
          >
            <MagnifyingGlassIcon className="w-5 h-5" />
          </button>
        </form>
      </div>
    </>
  );
};

export default Header;
